export interface ParsedRoleSpec {
  title: string;
  department: string;
  industry: string;
  experience: string;
  budgetMin: string;
  budgetMax: string;
  location: string;
  mode: "Remote" | "Hybrid" | "On-site";
  summary: string;
  requiredSkills: string[];
  bonusSkills: string[];
  interviewLanguages: string[];
  tags: string[];
  visa: string;
  noticePeriod: string;
  verticalKey: "software_engineering" | "healthcare" | "sales" | "retail_ops" | "general";
  levels: ("L1" | "L2" | "L3")[];
}

export interface CompanyContext {
  name?: string | null;
  industry?: string | null;
  region?: string | null;
  currency?: string | null;
  website?: string | null;
}

interface SkillRule {
  name: string;
  pattern: RegExp;
  vertical?: "software_engineering" | "healthcare" | "sales" | "retail_ops" | "general";
}

const SKILL_RULES: SkillRule[] = [
  // Healthcare
  { name: "Critical Care", pattern: /\bcritical\s*care\b/i, vertical: "healthcare" },
  { name: "Patient Triage", pattern: /\b(?:patient\s*)?triage\b/i, vertical: "healthcare" },
  { name: "Ventilator Protocols", pattern: /\bventilator\b/i, vertical: "healthcare" },
  { name: "ACLS/BLS", pattern: /\b(?:acls|bls)\b/i, vertical: "healthcare" },
  { name: "ICU Protocols", pattern: /\bicu\b/i, vertical: "healthcare" },
  { name: "Clinical Nursing", pattern: /\b(?:nurse|nursing|clinical)\b/i, vertical: "healthcare" },

  // Sales & Revenue
  { name: "Enterprise Sales", pattern: /\b(?:enterprise\s*)?sales\b/i, vertical: "sales" },
  { name: "B2B Prospecting", pattern: /\bb2b\b/i, vertical: "sales" },
  { name: "SaaS Negotiation", pattern: /\b(?:negotiation|closing)\b/i, vertical: "sales" },
  { name: "Pipeline Management", pattern: /\bpipeline\b/i, vertical: "sales" },
  { name: "Salesforce / CRM", pattern: /\b(?:salesforce|crm)\b/i, vertical: "sales" },

  // Customer Support & Ops
  { name: "Customer Service", pattern: /\b(?:customer\s*support|customer\s*service)\b/i, vertical: "retail_ops" },
  { name: "Zendesk", pattern: /\bzendesk\b/i, vertical: "retail_ops" },
  { name: "SLA Resolution", pattern: /\bsla\b/i, vertical: "retail_ops" },

  // Core Tech & Languages
  { name: "TypeScript", pattern: /\b(?:typescript|ts)\b/i, vertical: "software_engineering" },
  { name: "JavaScript", pattern: /\b(?:javascript|js)\b/i, vertical: "software_engineering" },
  { name: "Python", pattern: /\bpython\b/i, vertical: "software_engineering" },
  { name: "Java", pattern: /\bjava\b(?!script)/i, vertical: "software_engineering" },
  { name: ".NET Core / C#", pattern: /\b(?:\.net|dotnet|c#|csharp)\b/i, vertical: "software_engineering" },
  { name: "Go / Golang", pattern: /\b(?:golang|\bgo\b)\b/i, vertical: "software_engineering" },
  { name: "Rust", pattern: /\brust\b/i, vertical: "software_engineering" },
  { name: "PHP", pattern: /\bphp\b/i, vertical: "software_engineering" },
  { name: "Ruby on Rails", pattern: /\b(?:ruby|rails)\b/i, vertical: "software_engineering" },

  // Frontend
  { name: "React", pattern: /\breact(?:\.js)?\b(?! native)/i, vertical: "software_engineering" },
  { name: "Next.js", pattern: /\bnext(?:\.js)?\b/i, vertical: "software_engineering" },
  { name: "Vue.js", pattern: /\bvue(?:\.js)?\b/i, vertical: "software_engineering" },
  { name: "Angular", pattern: /\bangular\b/i, vertical: "software_engineering" },
  { name: "TailwindCSS", pattern: /\btailwind(?:css)?\b/i, vertical: "software_engineering" },
  { name: "HTML5 & Modern CSS", pattern: /\b(?:html5?|css3?|styling)\b/i, vertical: "software_engineering" },

  // Backend & APIs
  { name: "Node.js", pattern: /\bnode(?:\.js)?\b/i, vertical: "software_engineering" },
  { name: "NestJS", pattern: /\bnest(?:\.js)?\b/i, vertical: "software_engineering" },
  { name: "Django", pattern: /\bdjango\b/i, vertical: "software_engineering" },
  { name: "FastAPI", pattern: /\bfastapi\b/i, vertical: "software_engineering" },
  { name: "Spring Boot", pattern: /\bspring\s*boot\b/i, vertical: "software_engineering" },
  { name: "ASP.NET Core WebAPI", pattern: /\basp\.net|webapi\b/i, vertical: "software_engineering" },
  { name: "REST & GraphQL APIs", pattern: /\b(?:rest|restful|graphql|apis?)\b/i, vertical: "software_engineering" },
  { name: "Microservices", pattern: /\bmicroservices?\b/i, vertical: "software_engineering" },

  // Databases
  { name: "PostgreSQL", pattern: /\bpostgres(?:ql)?\b/i, vertical: "software_engineering" },
  { name: "SQL Server", pattern: /\bsql\s*server|mssql\b/i, vertical: "software_engineering" },
  { name: "MySQL", pattern: /\bmysql\b/i, vertical: "software_engineering" },
  { name: "MongoDB", pattern: /\bmongo(?:db)?\b/i, vertical: "software_engineering" },
  { name: "Redis", pattern: /\bredis\b/i, vertical: "software_engineering" },
  { name: "Kafka", pattern: /\bkafka\b/i, vertical: "software_engineering" },

  // Cloud & DevOps
  { name: "Kubernetes (EKS/K8s)", pattern: /\b(?:kubernetes|k8s)\b/i, vertical: "software_engineering" },
  { name: "Docker", pattern: /\bdocker\b/i, vertical: "software_engineering" },
  { name: "AWS Cloud", pattern: /\b(?:aws|amazon web services)\b/i, vertical: "software_engineering" },
  { name: "Azure Cloud", pattern: /\bazure\b/i, vertical: "software_engineering" },
  { name: "Terraform / IaC", pattern: /\b(?:terraform|iac)\b/i, vertical: "software_engineering" },
  { name: "CI/CD Automation", pattern: /\b(?:ci[\/-]?cd|github actions|pipelines?)\b/i, vertical: "software_engineering" },
];

/** Extract numerical experience target and map to clean display and level codes */
export function extractExperience(prompt: string): {
  experience: string;
  levels: ("L1" | "L2" | "L3")[];
  years: number;
} {
  const p = prompt.trim();

  // Pattern: "3-5 years", "3 to 5 yrs"
  const rangeMatch = p.match(/(\d+)\s*(?:-|–|to)\s*(\d+)\s*\+?\s*(?:years?|yrs?)/i);
  if (rangeMatch && rangeMatch[1] && rangeMatch[2]) {
    const min = parseInt(rangeMatch[1], 10);
    const max = parseInt(rangeMatch[2], 10);
    const levels: ("L1" | "L2" | "L3")[] =
      min <= 2 ? ["L1"] : min <= 4 ? ["L2"] : min <= 6 ? ["L2", "L3"] : ["L3"];
    return {
      experience: `${min}-${max} Years`,
      levels,
      years: min,
    };
  }

  // Pattern: "3+ years", "3+ yrs", "3 + years", "3+years"
  const plusMatch = p.match(/(\d+)\s*\+\s*(?:years?|yrs?)/i);
  if (plusMatch && plusMatch[1]) {
    const num = parseInt(plusMatch[1], 10);
    const levels: ("L1" | "L2" | "L3")[] =
      num <= 2 ? ["L1"] : num <= 4 ? ["L2"] : num <= 6 ? ["L2", "L3"] : ["L3"];
    return {
      experience: `${num}+ Years`,
      levels,
      years: num,
    };
  }

  // Pattern: "3 years experience", "min 3 years"
  const minMatch = p.match(/(?:min(?:imum)?|at least)\s*(\d+)\s*\+?\s*(?:years?|yrs?)/i);
  if (minMatch && minMatch[1]) {
    const num = parseInt(minMatch[1], 10);
    const levels: ("L1" | "L2" | "L3")[] =
      num <= 2 ? ["L1"] : num <= 4 ? ["L2"] : num <= 6 ? ["L2", "L3"] : ["L3"];
    return {
      experience: `${num}+ Years`,
      levels,
      years: num,
    };
  }

  const plainMatch = p.match(/(\d+)\s*(?:years?|yrs?)(?:\s*(?:of\s*)?(?:hands-on\s*)?experience)?/i);
  if (plainMatch && plainMatch[1]) {
    const num = parseInt(plainMatch[1], 10);
    const levels: ("L1" | "L2" | "L3")[] =
      num <= 2 ? ["L1"] : num <= 4 ? ["L2"] : num <= 6 ? ["L2", "L3"] : ["L3"];
    return {
      experience: `${num}+ Years`,
      levels,
      years: num,
    };
  }

  // Seniority fallback keywords if no explicit digit
  const lower = p.toLowerCase();
  if (lower.includes("intern") || lower.includes("graduate") || lower.includes("junior") || lower.includes("entry")) {
    return { experience: "1-3 Years", levels: ["L1"], years: 2 };
  }
  if (lower.includes("staff") || lower.includes("principal") || lower.includes("architect")) {
    return { experience: "7+ Years", levels: ["L3"], years: 7 };
  }
  if (lower.includes("lead") || lower.includes("director") || lower.includes("manager")) {
    return { experience: "8+ Years", levels: ["L3"], years: 8 };
  }
  if (lower.includes("senior")) {
    return { experience: "5+ Years (Job Target)", levels: ["L2", "L3"], years: 5 };
  }

  // Default
  return { experience: "3+ Years", levels: ["L2"], years: 3 };
}

/**
 * Section headings a pasted JD opens with — never a job title. "Role & responsibilities" was being
 * read as the title, and its first bullet ("Lead functional setup, …") after it.
 */
const JD_HEADING_RE =
  /^(?:(?:key\s+)?roles?\s*(?:&|and|\/)?\s*responsibilit\w*|responsibilit\w*|(?:job|role)\s+(?:description|summary|overview|purpose)|about\s+(?:the\s+)?(?:role|job|company|us)|(?:preferred|ideal|desired)\s+candidate(?:\s+profile)?|candidate\s+profile|requirements?|qualifications?|(?:key\s+|required\s+|must[-\s]have\s+)?skills?(?:\s*(?:&|and)\s*\w+)?|what\s+you(?:'ll|\s+will)\s+do|who\s+you\s+are|benefits|perks|experience|education|overview|summary|duties|desired\s+profile)\s*:?$/i;

/** Nouns a job title ends in. "Consulting" / "Engineering" etc. are normalised to the person. */
const ROLE_NOUN_RE =
  /^(?:consultant|consulting|developer|engineer|engineering|manager|analyst|architect|designer|nurse|teacher|tutor|specialist|administrator|admin|executive|accountant|officer|scientist|tester|technician|coordinator|associate|representative|recruiter|director|lead|head|programmer|counsellor|counselor|therapist|pharmacist|doctor|physician|auditor|advisor|adviser|strategist|writer|editor|operator|supervisor|assistant|agent|trainer|instructor|clerk|cashier|receptionist|marketer|owner|partner|expert)$/i;

const ROLE_NOUN_PERSON: Record<string, string> = {
  consulting: "Consultant",
  engineering: "Engineer",
  admin: "Administrator",
};

/** Words that may sit inside a title run without being capitalised. */
const TITLE_GLUE = new Set(["&", "/", "-", "of", "and", "for"]);

/** A token that can belong to a title: Capitalised, ALLCAPS, a number ("365"), or ".NET"-style. */
const isTitleToken = (w: string): boolean => /^(?:[A-Z][\w.+#-]*|\d[\w.+]*|\.[A-Za-z]+)$/.test(w);

/**
 * Find the role in a pasted job description: an explicit "Job title:" label first, then the longest
 * capitalised run that ends in a role noun ("…Business Central Functional Consulting" → "…Consultant"),
 * then a short opening line that isn't a section heading. Null when nothing looks like a title.
 */
function titleFromJobDescription(text: string): string | null {
  const lines = text
    .split("\n") // trim() below strips a CRLF's carriage return
    .map((l) => l.replace(/^(?:[\s*•·▪◦‣⁃–—>-]+|\d+[.)]\s+)+/, "").trim())
    .filter(Boolean);

  for (const line of lines) {
    const m = line.match(/^(?:job\s+title|position(?:\s+title)?|designation|role\s+title|title|role|position)\s*[:\-–]\s*(.{3,80})$/i);
    if (m?.[1] && !JD_HEADING_RE.test(m[1].trim())) return m[1].trim().replace(/[.,;:]+$/, "");
  }

  // A JD that opens with its title ("Staff Nurse – ICU") says it best — keep that line whole.
  const opener = lines.find((l) => !JD_HEADING_RE.test(l));
  const titleShaped = (l: string): boolean => l.split(/\s+/).length <= 8 && !/[.!?,;]/.test(l);
  if (opener && titleShaped(opener) && opener.split(/\s+/).some((w) => ROLE_NOUN_RE.test(w))) return opener;

  let best: string[] | null = null;
  for (const line of lines) {
    if (JD_HEADING_RE.test(line)) continue;
    const words = line.split(/\s+/);
    for (let i = 0; i < words.length; i++) {
      const raw = words[i]!.replace(/[^\w+#&/-]+$/, ""); // "Consulting." ends a sentence, not the noun
      if (!ROLE_NOUN_RE.test(raw) || !isTitleToken(raw)) continue;
      // Walk back over the capitalised run that qualifies the role noun.
      let j = i;
      while (j > 0) {
        const prev = words[j - 1]!;
        if (/[,;:.()]$/.test(prev)) break; // a clause boundary ends the run
        if (isTitleToken(prev) || (TITLE_GLUE.has(prev.toLowerCase()) && j - 1 > 0 && isTitleToken(words[j - 2]!))) j--;
        else break;
      }
      while (j < i && TITLE_GLUE.has(words[j]!.toLowerCase())) j++;
      // A bare role noun ("Lead functional setup…", "Manager") says nothing about the role.
      if (j === i) continue;
      const run = [...words.slice(j, i), ROLE_NOUN_PERSON[raw.toLowerCase()] ?? raw].slice(-9);
      if (!best || run.length > best.length) best = run;
    }
  }
  if (best) return best.join(" ");

  if (opener && titleShaped(opener)) return opener.replace(/[:]+$/, "");
  return null;
}

/** The text describes a software-building role — only then do the full-stack defaults apply. */
const SOFTWARE_ROLE_RE =
  /\b(software|developer|programmer|programming|full[- ]?stack|back[- ]?end|front[- ]?end|web\s+app|mobile\s+app|sde|swe|(?:software|platform|data|devops|site reliability)\s+engineer|engineer(?:ing)?\s+(?:role|team|manager)|node(?:\.js)?|java(?:script)?|golang|rust)\b/i;

/** A prompt that reads as a pasted job description rather than a one-line ask. */
export const looksLikeJobDescription = (text: string): boolean =>
  text.trim().includes("\n") || text.trim().split(/\s+/).length > 40;

/** Extract or format clean job title */
export function extractTitle(prompt: string, years: number): string {
  let text = prompt.trim();
  if (!text) return "Software Engineer";

  if (looksLikeJobDescription(text)) {
    const fromJd = titleFromJobDescription(text);
    if (fromJd) return fromJd;
    // Nothing title-shaped anywhere: the generic default beats a sentence of JD as the title.
    return years >= 5 ? "Senior Software Engineer" : "Software Engineer";
  }

  // Remove common prefix phrases
  text = text
    .replace(
      /^(?:requirement for|looking for|need a|need an|need|hiring for|hiring a|hiring an|hiring|we are looking for|we need|job spec for|job specification for|jd for|i need a|i need an|i need)\s+/i,
      "",
    )
    .trim();

  // Split on connectors like "with 3+ years", "in Chicago", "budget", "salary", "for our company"
  const segments = text.split(
    /(?:\s+for\s+our\s+company|\s+with\s+\d+|\s+in\s+[A-Z]|\s+for\s+[A-Z]|\s+budget\s+|\s+salary\s+|\s+offering\s+)/i,
  );
  let titleCandidate = (segments[0] || text).trim();

  // Remove trailing punct / comma
  titleCandidate = titleCandidate.replace(/[,;:]+$/, "").trim();

  // Clean out leading "a " or "an "
  titleCandidate = titleCandidate.replace(/^(?:a|an)\s+/i, "").trim();

  // If candidate is too short or empty, provide sensible title
  if (titleCandidate.length < 3) {
    titleCandidate = years >= 5 ? "Senior Software Engineer" : "Software Engineer";
  }

  // If user typed "3+ years React developer", turn into "React Developer"
  titleCandidate = titleCandidate.replace(/^\d+\+?\s*(?:years?|yrs?)\s+(?:experienced\s+)?/i, "");

  // Capitalize words nicely
  return titleCandidate
    .split(" ")
    .map((w) => {
      const lower = w.toLowerCase();
      if (["and", "or", "for", "in", "of", "with", "a", "an", "the"].includes(lower)) return lower;
      if (w.includes(".")) return w; // e.g. Node.js, Next.js, .NET
      if (w.startsWith("(")) return "(" + w.charAt(1).toUpperCase() + w.slice(2);
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(" ");
}

/** Cities the local parser recognises by name. */
const CITIES: Record<string, string> = {
  chicago: "Chicago, IL",
  dallas: "Dallas, TX",
  austin: "Austin, TX",
  "new york": "New York, NY",
  nyc: "New York, NY",
  "san francisco": "San Francisco, CA",
  sf: "San Francisco, CA",
  seattle: "Seattle, WA",
  boston: "Boston, MA",
  bangalore: "Bangalore, India",
  bengaluru: "Bangalore, India",
  pune: "Pune, India",
  mumbai: "Mumbai, India",
  hyderabad: "Hyderabad, India",
  delhi: "Delhi NCR, India",
  noida: "Noida, India",
  gurgaon: "Gurgaon, India",
  chennai: "Chennai, India",
  london: "London, UK",
};

/** True when the text names a city we recognise — a stated location beats a remembered one. */
export function namesKnownCity(prompt: string): boolean {
  const p = prompt.toLowerCase();
  return Object.keys(CITIES).some((key) => p.includes(key));
}

/** Extract location and workplace mode */
export function extractLocationAndMode(prompt: string, companyContext?: CompanyContext): {
  location: string;
  mode: "Remote" | "Hybrid" | "On-site";
} {
  const p = prompt.toLowerCase();

  let mode: "Remote" | "Hybrid" | "On-site" = "Remote";
  if (p.includes("hybrid")) {
    mode = "Hybrid";
  } else if (p.includes("onsite") || p.includes("on-site") || p.includes("in-office") || p.includes("hospital")) {
    mode = "On-site";
  } else if (p.includes("remote")) {
    mode = "Remote";
  }


  let foundCity = "";
  for (const [key, val] of Object.entries(CITIES)) {
    if (p.includes(key)) {
      foundCity = val;
      break;
    }
  }

  if (foundCity) {
    return {
      location: foundCity,
      mode,
    };
  }

  if (p.includes("india") || (companyContext?.region === "in" || companyContext?.currency === "INR")) {
    return { location: "Bangalore, India", mode };
  }

  return {
    // The location is the PLACE only — every display appends the mode itself, so a "(Hybrid)"
    // baked in here rendered as "New York, NY (Hybrid) (Hybrid)".
    location: mode === "Remote" ? "Chicago, IL" : "New York, NY",
    mode,
  };
}

/** Extract budget or assign realistic default based on experience & currency */
export function extractBudget(
  prompt: string,
  years: number,
  isIndia: boolean,
): {
  budgetMin: string;
  budgetMax: string;
} {
  const p = prompt.toLowerCase();

  // Pattern: "$120,000 - $150,000" or "$120k - $150k" or "$ 120k to 150k"
  const dollarRange = prompt.match(/\$\s*(\d+[\d,]*\s*k?)\s*(?:-|–|to)\s*\$?\s*(\d+[\d,]*\s*k?)/i);
  if (dollarRange && dollarRange[1] && dollarRange[2]) {
    const parseAmt = (s: string) => {
      const clean = s.replace(/[\$,\s]/g, "");
      if (clean.toLowerCase().endsWith("k")) {
        return parseInt(clean, 10) * 1000;
      }
      return parseInt(clean, 10);
    };
    const min = parseAmt(dollarRange[1]);
    const max = parseAmt(dollarRange[2]);
    return {
      budgetMin: `$ ${min.toLocaleString("en-US")}`,
      budgetMax: `$ ${max.toLocaleString("en-US")}`,
    };
  }

  // Pattern: "₹12L - ₹18L" or "12-18 LPA" or "12 to 18 Lakhs"
  const inrRange = prompt.match(/(?:₹\s*)?(\d+)\s*(?:L|Lakhs?)?\s*(?:-|–|to)\s*(?:₹\s*)?(\d+)\s*(?:L|LPA|Lakhs?)/i);
  if (inrRange && inrRange[1] && inrRange[2]) {
    const minL = parseInt(inrRange[1], 10);
    const maxL = parseInt(inrRange[2], 10);
    return {
      budgetMin: `₹ ${(minL * 100000).toLocaleString("en-IN")}`,
      budgetMax: `₹ ${(maxL * 100000).toLocaleString("en-IN")}`,
    };
  }

  // Nothing stated → no budget. Budget is optional; a range guessed from seniority or location
  // (this used to return e.g. ₹22,00,000 – ₹30,00,000 for 5+ years) reads as the recruiter's own number.
  void years;
  void isIndia;
  return { budgetMin: "", budgetMax: "" };
}

/**
 * Department rules, first match wins. Values are DEPARTMENT_OPTIONS entries (components/
 * SearchableSelect.tsx) so the dropdown shows a real option. Whole words only — the old substring
 * checks matched "ui" inside "build" and "care" inside "careers", so a QA tester's JD came out as
 * "Product Experience" (14 Oct 2026).
 */
const DEPARTMENT_RULES: { dept: string; re: RegExp }[] = [
  { dept: "Quality Assurance & Testing", re: /\b(qa|quality assurance|quality engineer\w*|tester|testing|test (?:engineer|analyst|lead|automation)|sdet)\b/i },
  { dept: "Healthcare & Clinical Care", re: /\b(nurs\w*|icu|clinical|doctor|physician|medical officer|pharmacist|patient care)\b/i },
  { dept: "Sales & Business Development", re: /\b(sales|business development|account executive|account manager|bdm?|pre-?sales)\b/i },
  { dept: "Customer Support & Operations", re: /\b(customer (?:support|service|success|care)|support (?:executive|engineer|agent|specialist)|call cent(?:er|re)|helpdesk|bpo)\b/i },
  { dept: "Design & UX", re: /\b(ux|ui\/ux|product designer|graphic designer|visual designer|ui designer|designer)\b/i },
  { dept: "Data Science & Analytics", re: /\b(data scien\w*|data analyst|machine learning|ml engineer|analytics|business intelligence|bi developer)\b/i },
  { dept: "Marketing & Growth", re: /\b(marketing|growth|seo|content writer|social media|brand manager)\b/i },
  { dept: "Human Resources & Talent", re: /\b(hr|human resources|recruiter|talent acquisition|payroll)\b/i },
  { dept: "Finance & Accounting", re: /\b(accountant|accounting|finance (?:manager|executive|analyst)|financial analyst|auditor|chartered accountant|tax)\b/i },
  { dept: "Product Management", re: /\b(product manager|product owner|product management)\b/i },
  { dept: "Legal & Compliance", re: /\b(legal|lawyer|counsel|compliance)\b/i },
  { dept: "Operations & Supply Chain", re: /\b(supply chain|logistics|warehouse|procurement|operations manager)\b/i },
  { dept: "Technology & Architecture", re: /\b(architect)\b/i },
  { dept: "Engineering", re: /\b(engineer\w*|developer|devops|sre|programmer|full ?stack|frontend|front-end|backend|back-end)\b/i },
];

/** Determine role department — from the job title first; the description only when the title says nothing. */
export function inferDepartment(title: string, prompt: string): string {
  for (const text of [title, prompt]) {
    const hit = DEPARTMENT_RULES.find((r) => r.re.test(text));
    if (hit) return hit.dept;
  }
  return "Engineering";
}

/**
 * The part of a pasted JD that describes the company — "About the company / About us" up to the next
 * heading — else its opening lines. A requirement buried in "Nice to have: experience testing
 * payments" must not make the whole company FinTech.
 */
function companyPart(prompt: string): string {
  if (!looksLikeJobDescription(prompt)) return prompt;
  const lines = prompt.split("\n");
  const start = lines.findIndex((l) => /^\s*#*\s*about\s+(?:the\s+)?(?:company|us|organi[sz]ation)\b/i.test(l));
  if (start < 0) return prompt.slice(0, 300);
  const body: string[] = [];
  for (const l of lines.slice(start + 1)) {
    if (/^\s*#/.test(l) || JD_HEADING_RE.test(l.trim())) break;
    body.push(l);
  }
  return body.join("\n");
}

/** Determine vertical key */
export function inferVerticalKey(
  title: string,
  prompt: string,
): "software_engineering" | "healthcare" | "sales" | "retail_ops" | "general" {
  const t = (title + " " + prompt).toLowerCase();
  if (t.includes("nurse") || t.includes("icu") || t.includes("clinical") || t.includes("doctor"))
    return "healthcare";
  if (t.includes("sales") || t.includes("b2b") || t.includes("account executive"))
    return "sales";
  if (t.includes("support") || t.includes("retail") || t.includes("customer service"))
    return "retail_ops";
  if (
    t.includes("developer") ||
    t.includes("engineer") ||
    t.includes("frontend") ||
    t.includes("backend") ||
    t.includes("devops") ||
    t.includes("full stack") ||
    t.includes(".net") ||
    t.includes("react") ||
    t.includes("node")
  ) {
    return "software_engineering";
  }
  return "general";
}

/** Extract interview languages (e.g. English, Tamil, Hindi, etc.) */
export function extractInterviewLanguages(prompt: string): string[] {
  const p = prompt.toLowerCase();
  const found: string[] = ["en"];

  const langMap: Record<string, string> = {
    tamil: "ta",
    hindi: "hi",
    telugu: "te",
    kannada: "kn",
    malayalam: "ml",
    marathi: "mr",
    gujarati: "gu",
    bengali: "bn",
    french: "fr",
    spanish: "es",
    dutch: "nl",
  };

  for (const [name, code] of Object.entries(langMap)) {
    if (p.includes(name)) {
      if (!found.includes(code)) found.push(code);
    }
  }

  return found;
}

/** Extract or infer industry domain */
export function extractIndustry(prompt: string, title: string, companyIndustry?: string | null): string {
  if (companyIndustry && companyIndustry.trim()) {
    return companyIndustry.trim();
  }

  // Title + the company part of the text only (see companyPart). Values match INDUSTRY_OPTIONS.
  const p = `${title} ${companyPart(prompt)}`;
  if (/\b(?:fintech|payments? (?:company|platform|gateway)|banking|bank|nbfc|insurance)\b/i.test(p)) return "Financial Services / FinTech";
  if (/\b(?:healthcare|hospital|clinical|nurse|medical|health)\b/i.test(p)) return "Healthcare & Life Sciences";
  if (/\b(?:ecommerce|e-commerce|retail|shopping)\b/i.test(p)) return "E-Commerce & Retail";
  if (/\b(?:edtech|education|school|university)\b/i.test(p)) return "Education & EdTech";
  if (/\b(?:saas|b2b software)\b/i.test(p)) return "Enterprise Software / SaaS";
  if (/\b(?:machine learning|artificial intelligence|data platform)\b/i.test(p)) return "AI & Data Platforms";

  return "Technology & Software";
}

/** Generate relevant tags */
export function extractTags(
  title: string,
  prompt: string,
  requiredSkills: string[],
  mode: string,
): string[] {
  const tags = new Set<string>();
  tags.add(mode);
  tags.add("Full-time");

  const p = (title + " " + prompt).toLowerCase();
  if (p.includes("senior") || p.includes("lead") || p.includes("staff")) tags.add("Senior Level");
  else if (p.includes("junior") || p.includes("entry") || p.includes("intern")) tags.add("Early Career");
  else tags.add("Mid Level");

  if (p.includes("frontend") || p.includes("ui") || p.includes("react")) tags.add("Frontend");
  if (p.includes("backend") || p.includes("api") || p.includes("node")) tags.add("Backend");
  if (p.includes("full stack") || p.includes("fullstack")) tags.add("Full Stack");
  if (p.includes("devops") || p.includes("cloud")) tags.add("Cloud / DevOps");
  if (p.includes("fintech") || p.includes("payment")) tags.add("FinTech");

  for (const s of requiredSkills.slice(0, 3)) {
    tags.add(s);
  }

  return Array.from(tags).slice(0, 6);
}

/** Main dynamic NLP specification generator with company memory support */
export function parseDynamicPrompt(promptText: string, companyContext?: CompanyContext): ParsedRoleSpec {
  const prompt = promptText.trim();
  const { experience, levels, years } = extractExperience(prompt);
  const title = extractTitle(prompt, years);
  const isIndia =
    prompt.toLowerCase().includes("india") ||
    prompt.toLowerCase().includes("bangalore") ||
    prompt.toLowerCase().includes("pune") ||
    prompt.toLowerCase().includes("₹") ||
    prompt.toLowerCase().includes("lpa") ||
    companyContext?.region === "in" ||
    companyContext?.currency === "INR";

  const { location, mode } = extractLocationAndMode(prompt, companyContext);
  const { budgetMin, budgetMax } = extractBudget(prompt, years, isIndia);
  const department = inferDepartment(title, prompt);
  const verticalKey = inferVerticalKey(title, prompt);
  const industry = extractIndustry(prompt, title, companyContext?.industry);
  const interviewLanguages = extractInterviewLanguages(prompt);

  // Scan skills from prompt
  const matchedSkills: string[] = [];
  for (const rule of SKILL_RULES) {
    if (rule.pattern.test(prompt)) {
      if (!matchedSkills.includes(rule.name)) {
        matchedSkills.push(rule.name);
      }
    }
  }

  // If few skills detected, augment based on vertical and title
  const requiredSkills = [...matchedSkills];
  const bonusSkills: string[] = [];

  if (verticalKey === "healthcare") {
    const defaults = ["Critical Care", "Patient Triage", "Ventilator Protocols", "ACLS/BLS"];
    for (const d of defaults) {
      if (!requiredSkills.includes(d)) requiredSkills.push(d);
    }
    bonusSkills.push("Electronic Health Records (EHR)", "Emergency Code Response", "Patient Advocacy");
  } else if (verticalKey === "sales") {
    const defaults = ["Enterprise Sales", "B2B Prospecting", "SaaS Negotiation", "Pipeline Management"];
    for (const d of defaults) {
      if (!requiredSkills.includes(d)) requiredSkills.push(d);
    }
    bonusSkills.push("Salesforce / CRM", "Contract Closing", "Executive Presentations");
  } else {
    // Software Engineering defaults tailored to detected tech
    const p = prompt.toLowerCase();
    if (p.includes(".net") || p.includes("c#")) {
      const defaults = [".NET Core / C#", "ASP.NET Core WebAPI", "SQL Server", "Microservices", "Azure Cloud"];
      for (const d of defaults) {
        if (!requiredSkills.includes(d)) requiredSkills.push(d);
      }
      bonusSkills.push("Kafka / Event Sourcing", "Docker / K8s", "Entity Framework Core", "Redis");
    } else if (p.includes("frontend") || p.includes("react")) {
      const defaults = ["React", "TypeScript", "Next.js", "TailwindCSS", "State Management & Testing"];
      for (const d of defaults) {
        if (!requiredSkills.includes(d)) requiredSkills.push(d);
      }
      bonusSkills.push("Webpack / Vite", "Jest & Playwright", "GraphQL", "Performance Tuning");
    } else if (p.includes("devops") || p.includes("cloud") || p.includes("infra") || p.includes("sre")) {
      const defaults = ["Kubernetes (EKS/K8s)", "Terraform / IaC", "AWS Cloud", "Docker", "CI/CD Automation"];
      for (const d of defaults) {
        if (!requiredSkills.includes(d)) requiredSkills.push(d);
      }
      bonusSkills.push("Prometheus / Grafana", "GitOps (ArgoCD)", "Vault / Security", "Datadog Telemetry");
    } else if (p.includes("python") || p.includes("django") || p.includes("fastapi")) {
      const defaults = ["Python", "FastAPI / Django", "PostgreSQL", "REST & GraphQL APIs", "Docker"];
      for (const d of defaults) {
        if (!requiredSkills.includes(d)) requiredSkills.push(d);
      }
      bonusSkills.push("Redis Caching", "Celery / RabbitMQ", "AWS Architecture", "PyTest");
    } else if (!SOFTWARE_ROLE_RE.test(prompt)) {
      // Not a software role: leave the list to what the text names (the AI read fills it) or to
      // the recruiter. Gating on "no skill matched" was not enough — a Business Central JD that
      // mentions "CRM" matched one skill, fell through to the full-stack defaults below, and its
      // interview was generated against TypeScript / Next.js / Docker / Prisma (4 Oct 2026).
    } else {
      // General full stack
      const defaults = ["TypeScript", "Next.js / React", "Node.js", "PostgreSQL", "REST & GraphQL APIs"];
      for (const d of defaults) {
        if (!requiredSkills.includes(d)) requiredSkills.push(d);
      }
      bonusSkills.push("Docker Containerization", "Redis Caching", "Cloud Infrastructure (AWS/Azure)", "Prisma ORM");
    }
  }

  const tags = extractTags(title, prompt, requiredSkills, mode);

  // The role summary becomes the job description the interview is generated and scored against,
  // so it may only say what the recruiter said. A pasted JD IS the summary, word for word. A one-
  // line ask gets one plain sentence. The old template ("…leverage hands-on expertise in X to
  // design resilient architecture… deliver high-impact production capabilities") was appended to
  // every requirement, and on a Business Central role it put a Salesforce / TypeScript / Next.js
  // engineering brief into the job description the questions and the judge read (4 Oct 2026).
  const coreSkillList = requiredSkills.slice(0, 3).join(", ");
  const companyMention = companyContext?.name ? ` for ${companyContext.name}` : "";
  const summary = looksLikeJobDescription(prompt)
    ? prompt.slice(0, 8000)
    : `Seeking a ${title} with ${experience} for our ${department} team${companyMention}.` +
      (coreSkillList ? ` Key skills: ${coreSkillList}.` : "");

  return {
    title,
    department,
    industry,
    experience,
    budgetMin,
    budgetMax,
    location,
    mode,
    summary,
    requiredSkills: requiredSkills.slice(0, 6),
    bonusSkills: bonusSkills.slice(0, 4),
    interviewLanguages,
    tags,
    visa: "Eligible",
    noticePeriod: "Max 4 weeks",
    verticalKey,
    levels,
  };
}

/** What POST /requisitions/requirement/parse returns — only what the JD states; nulls elsewhere. */
export interface AiParsedJd {
  title: string | null;
  requiredSkills: string[];
  bonusSkills: string[];
  minYears: number | null;
  maxYears: number | null;
  location: string | null;
  workplaceMode: "Remote" | "Hybrid" | "On-site" | null;
  budgetMin: number | null;
  budgetMax: number | null;
  currency: "INR" | "USD" | null;
  interviewLanguages: string[];
  department: string | null;
  industry: string | null;
  verticalKey: string | null;
  summary: string | null;
}

const levelsForYears = (n: number): ("L1" | "L2" | "L3")[] =>
  n <= 2 ? ["L1"] : n <= 4 ? ["L2"] : n <= 6 ? ["L2", "L3"] : ["L3"];

/**
 * Lay the AI's reading of a JD over the local parse. The AI wins wherever it found something;
 * where it found nothing the local value stays (the chat still asks for experience when the JD
 * gave no years). Skills are REPLACED, not merged — the local list is exactly the wrong guess this
 * exists to correct.
 */
export function mergeAiSpec(local: ParsedRoleSpec, ai: AiParsedJd): ParsedRoleSpec {
  const next: ParsedRoleSpec = { ...local };
  if (ai.title) next.title = ai.title;
  if (ai.requiredSkills.length > 0) next.requiredSkills = ai.requiredSkills;
  if (ai.requiredSkills.length > 0 || ai.bonusSkills.length > 0) next.bonusSkills = ai.bonusSkills;
  if (ai.minYears !== null) {
    next.experience =
      ai.maxYears !== null && ai.maxYears > ai.minYears ? `${ai.minYears}-${ai.maxYears} Years` : `${ai.minYears}+ Years`;
    next.levels = levelsForYears(ai.minYears);
  }
  if (ai.location) next.location = ai.location.replace(/\s*\((?:Remote|Hybrid|On-site)\)/gi, "").trim();
  if (ai.workplaceMode) next.mode = ai.workplaceMode;
  if (ai.budgetMin && ai.budgetMax) {
    const inr = (ai.currency ?? (local.budgetMin.startsWith("₹") ? "INR" : "USD")) === "INR";
    const fmt = (n: number) => (inr ? `₹ ${n.toLocaleString("en-IN")}` : `$ ${n.toLocaleString("en-US")}`);
    next.budgetMin = fmt(ai.budgetMin);
    next.budgetMax = fmt(ai.budgetMax);
  }
  if (ai.interviewLanguages.length > 0) next.interviewLanguages = [...new Set(["en", ...ai.interviewLanguages])];
  if (ai.department) next.department = ai.department;
  if (ai.industry) next.industry = ai.industry;
  if (ai.verticalKey) next.verticalKey = ai.verticalKey as ParsedRoleSpec["verticalKey"];
  if (ai.summary) next.summary = ai.summary;
  next.tags = extractTags(next.title, next.summary, next.requiredSkills, next.mode);
  return next;
}
